export const createPopupController = ({
  state,
  popup,
  popupKicker,
  popupTitle,
  popupMeta,
  popupAttributes,
  selectedPlacementMappings,
  selectedNodeMappings,
  apiRequest,
  escapeHtml,
  formatDetailValue,
  formatPosition,
  normalizePlacementVerticalMode,
  renderLiveDetailBlocks
}) => {
  const controller = {}

  const popupIntegrationMappings = () =>
    state.popupEntry?.kind === "placement"
      ? Array.from(selectedPlacementMappings())
      : Array.from(selectedNodeMappings())

  const popupTabOptions = () => ([
    { key: "status", label: "Status" },
    { key: "actions", label: "Actions" },
    { key: "attributes", label: "Asset" },
    { key: "maintain", label: "Maintain" },
    { key: "object", label: "Object" }
  ])

  const normalizePopupTab = (value) =>
    popupTabOptions().some((option) => option.key === value) ? value : "status"

  const defaultPopupMaintainDraft = () => ({
    service_provider: "",
    service_contact: "",
    service_email: "",
    service_phone: "",
    note: "",
    resource_link: ""
  })

  const defaultPopupTabForMappings = (integrationMappings, actionOptions) => {
    const hasLiveDetails = Array.from(integrationMappings || []).some((mapping) => Boolean(mapping?.details_url))
    const hasActions = Array.from(actionOptions || []).length > 0
    return !hasLiveDetails && !hasActions ? "attributes" : "status"
  }

  const loadPopupLiveDetails = () => {
    popupIntegrationMappings().forEach((mapping) => {
      if (!mapping?.id || !mapping?.details_url) return

      const currentState = state.liveDetailsByMappingId.get(mapping.id)
      if (currentState?.status === "loading" || currentState?.status === "success") return

      state.liveDetailsByMappingId.set(mapping.id, { status: "loading", details: null, error: null })
      controller.renderPopup()

      apiRequest(mapping.details_url)
        .then((payload) => {
          state.liveDetailsByMappingId.set(mapping.id, {
            status: "success",
            details: payload.details || { rows: [] },
            error: null
          })
          controller.renderPopup()
        })
        .catch((error) => {
          state.liveDetailsByMappingId.set(mapping.id, {
            status: "error",
            details: null,
            error: error.message
          })
          controller.renderPopup()
        })
    })
  }

  const refreshPopupLiveDetails = () => {
    popupIntegrationMappings().forEach((mapping) => {
      if (!mapping?.id || !mapping?.details_url) return

      state.liveDetailsByMappingId.delete(mapping.id)
    })

    loadPopupLiveDetails()
  }

  const popupActionMessage = () => {
    if (!state.popupActionFeedback?.message) return ""

    return `
      <div class="plan-viewer-popup-actions-feedback" data-tone="${escapeHtml(state.popupActionFeedback.tone || "neutral")}">
        ${escapeHtml(state.popupActionFeedback.message)}
        ${state.popupActionFeedback.openUrl
          ? `
            <div class="plan-viewer-popup-actions-feedback-link">
              <a href="${escapeHtml(state.popupActionFeedback.openUrl)}" target="_blank" rel="noopener">${escapeHtml(state.popupActionFeedback.openLabel || "Open live view")}</a>
            </div>
          `
          : ""}
      </div>
    `
  }

  const popupActionInlineEmbedMarkup = () => {
    if (!state.popupActionFeedback?.inlineEmbedUrl) return ""

    return `
      <div class="plan-viewer-popup-actions-inline-embed">
        <div class="plan-viewer-popup-actions-inline-embed-head">
          <span>${escapeHtml(state.popupActionFeedback.inlineEmbedLabel || "Preview")}</span>
          <button type="button" class="plan-viewer-popup-actions-inline-embed-close" data-popup-inline-embed-close aria-label="Close preview">Close</button>
        </div>
        <div class="plan-viewer-popup-actions-inline-embed-frame">
          <iframe
            src="${escapeHtml(state.popupActionFeedback.inlineEmbedUrl)}"
            title="${escapeHtml(state.popupActionFeedback.inlineEmbedLabel || "Preview")}"
            loading="lazy"
            referrerpolicy="strict-origin-when-cross-origin"
            allowfullscreen>
          </iframe>
        </div>
      </div>
    `
  }

  const popupActionStateKey = (actionValue, fieldKey) => `${actionValue}:${fieldKey}`

  const popupActionInitialValue = (actionValue, field) => {
    const stateKey = popupActionStateKey(actionValue, field.key)
    if (Object.prototype.hasOwnProperty.call(state.popupActionValues, stateKey)) {
      return state.popupActionValues[stateKey]
    }

    if (field.value === true) return "true"
    if (field.value === false) return "false"
    return field.value == null ? "" : String(field.value)
  }

  const popupActionFieldMarkup = (actionValue, field) => {
    const fieldId = `plan-viewer-popup-action-field-${actionValue}-${field.key}`.replace(/[^a-zA-Z0-9_-]/g, "-")
    const initialValue = popupActionInitialValue(actionValue, field)

    return `
      <label class="plan-viewer-popup-actions-label" for="${escapeHtml(fieldId)}">
        ${escapeHtml(field.label || field.key)}
      </label>
      ${field.type === "select"
        ? `
          <select
            id="${escapeHtml(fieldId)}"
            class="plan-mapping-input"
            data-popup-action-key="${escapeHtml(actionValue)}"
            data-popup-action-field="${escapeHtml(field.key)}"
            ${field.required ? "required" : ""}>
            <option value=""></option>
            ${Array.from(field.options || []).map((option) => `
              <option value="${escapeHtml(option.value)}"${option.value === initialValue ? " selected" : ""}>${escapeHtml(option.label)}</option>
            `).join("")}
          </select>
        `
        : field.type === "checkbox"
          ? `
            <label class="plan-viewer-popup-actions-toggle" for="${escapeHtml(fieldId)}">
              <input
                id="${escapeHtml(fieldId)}"
                class="plan-toggle-input"
                data-popup-action-key="${escapeHtml(actionValue)}"
                data-popup-action-field="${escapeHtml(field.key)}"
                type="checkbox"
                ${initialValue === "true" ? "checked" : ""}
              >
              <span class="plan-toggle-slider" aria-hidden="true"></span>
            </label>
          `
          : field.type === "range"
            ? `
              <div class="plan-viewer-popup-actions-range">
                <input
                  id="${escapeHtml(fieldId)}"
                  data-popup-action-key="${escapeHtml(actionValue)}"
                  data-popup-action-field="${escapeHtml(field.key)}"
                  type="range"
                  value="${escapeHtml(initialValue)}"
                  ${field.min != null ? `min="${escapeHtml(String(field.min))}"` : ""}
                  ${field.max != null ? `max="${escapeHtml(String(field.max))}"` : ""}
                  ${field.step != null ? `step="${escapeHtml(String(field.step))}"` : ""}
                  ${field.required ? "required" : ""}
                >
                <span class="plan-viewer-popup-actions-range-value" data-popup-action-range-value="${escapeHtml(actionValue)}:${escapeHtml(field.key)}">${escapeHtml(initialValue || "0")}</span>
              </div>
            `
        : `
          <input
            id="${escapeHtml(fieldId)}"
            class="plan-mapping-input"
            data-popup-action-key="${escapeHtml(actionValue)}"
            data-popup-action-field="${escapeHtml(field.key)}"
            type="${escapeHtml(field.type === "password" ? "password" : field.type === "number" ? "number" : "text")}"
            value="${escapeHtml(initialValue)}"
            ${field.min != null ? `min="${escapeHtml(String(field.min))}"` : ""}
            ${field.max != null ? `max="${escapeHtml(String(field.max))}"` : ""}
            ${field.step != null ? `step="${escapeHtml(String(field.step))}"` : ""}
            ${field.max_length != null ? `maxlength="${escapeHtml(String(field.max_length))}"` : ""}
            ${field.placeholder ? `placeholder="${escapeHtml(field.placeholder)}"` : ""}
            ${field.required ? "required" : ""}
          >
        `}
      ${field.help ? `<p class="plan-viewer-popup-actions-help">${escapeHtml(field.help)}</p>` : ""}
    `
  }

  const popupActionFieldsMarkup = (actionValue, definition) => {
    const fields = Array.from(definition?.fields || [])
    if (fields.length === 0) return ""

    return `
      <div class="plan-viewer-popup-actions-fields">
        ${fields.map((field) => popupActionFieldMarkup(actionValue, field)).join("")}
      </div>
    `
  }

  const popupActionFieldInput = (actionValue, field) =>
    Array.from(popup?.querySelectorAll("[data-popup-action-field]") || [])
      .find((input) => input.dataset.popupActionKey === actionValue && input.dataset.popupActionField === field.key)

  const popupActionFieldValue = (actionValue, field) => {
    const input = popupActionFieldInput(actionValue, field)
    if (field.type === "checkbox") {
      return input?.checked ? "true" : "false"
    }

    return input?.value?.toString() || ""
  }

  const popupActionFieldsValid = (actionValue, definition) => {
    const fields = Array.from(definition?.fields || [])

    const groupValues = new Map()

    const fieldsAreValid = fields.every((field) => {
      const input = popupActionFieldInput(actionValue, field)
      if (!input) return !field.required

      const value = popupActionFieldValue(actionValue, field)
      if (field.one_of_group) {
        groupValues.set(field.one_of_group, (groupValues.get(field.one_of_group) || 0) + (value.trim().length > 0 ? 1 : 0))
      }
      if (field.required && value.trim().length === 0) return false
      if (field.max_length != null && value.length > Number(field.max_length)) return false
      if (typeof input.checkValidity === "function" && !input.checkValidity()) return false

      return true
    })

    if (!fieldsAreValid) return false

    const requiredGroups = Array.from(new Set(fields.map((field) => field.one_of_group).filter(Boolean)))
    return requiredGroups.every((group) => (groupValues.get(group) || 0) > 0)
  }

  const popupActionPayload = (actionValue, definition) => {
    const fields = Array.from(definition?.fields || [])
    const payload = {}

    for (const field of fields) {
      const value = popupActionFieldValue(actionValue, field)
      state.popupActionValues[popupActionStateKey(actionValue, field.key)] = value

      if (field.required && value.trim().length === 0) {
        throw new Error(`${field.label || field.key} is required.`)
      }
      if (field.max_length != null && value.length > Number(field.max_length)) {
        throw new Error(`${field.label || field.key} must be no more than ${field.max_length} characters.`)
      }

      if (field.type === "checkbox") {
        payload[field.key] = value === "true"
        continue
      }

      if (field.type === "number" || field.type === "range") {
        if (value.trim().length === 0) continue
        const numericValue = Number(value)
        if (!Number.isFinite(numericValue)) {
          throw new Error(`${field.label || field.key} must be a valid number.`)
        }
        if (field.min != null && numericValue < Number(field.min)) {
          throw new Error(`${field.label || field.key} must be at least ${field.min}.`)
        }
        if (field.max != null && numericValue > Number(field.max)) {
          throw new Error(`${field.label || field.key} must be no more than ${field.max}.`)
        }
      }

      if (value.trim().length > 0) {
        payload[field.key] = value
      }
    }

    const requiredGroups = Array.from(new Set(fields.map((field) => field.one_of_group).filter(Boolean)))
    requiredGroups.forEach((group) => {
      const hasValue = fields.some((field) => field.one_of_group === group && popupActionFieldValue(actionValue, field).trim().length > 0)
      if (!hasValue) {
        throw new Error("At least one field must be provided.")
      }
    })

    if (fields.length > 0 && Object.keys(payload).length === 0) {
      throw new Error("At least one field must be provided.")
    }

    return payload
  }

  const openActionSequence = (urls = []) => {
    const sequence = Array.from(urls).filter((url) => typeof url === "string" && url.length > 0)
    if (sequence.length === 0) return false

    const popupWindow = window.open("", "_blank")
    if (!popupWindow) return false

    try {
      popupWindow.opener = null
      popupWindow.document.write(`
        <!doctype html>
        <html lang="en">
          <head>
            <meta charset="utf-8">
            <title>Opening Live Stream</title>
            <style>
              body {
                margin: 0;
                min-height: 100vh;
                display: grid;
                place-items: center;
                background: #0f172a;
                color: #f8fafc;
                font: 600 16px/1.5 system-ui, sans-serif;
              }
              .message {
                max-width: 28rem;
                padding: 1.5rem;
                text-align: center;
              }
            </style>
          </head>
          <body>
            <div class="message">Preparing the Eagle Eye live stream…</div>
          </body>
        </html>
      `)
      popupWindow.document.close()
    } catch (_error) {
      // Ignore if the browser prevents writing to the popup document.
    }

    popupWindow.location.replace(sequence[0])

    if (sequence.length > 1) {
      window.setTimeout(() => {
        if (!popupWindow.closed) {
          popupWindow.location.replace(sequence[1])
        }
      }, 1800)
    }

    return true
  }

  const executePopupAction = async (actionValue) => {
    if (!popup) return

    const goButton = Array.from(popup.querySelectorAll("[data-popup-action-run]") || [])
      .find((button) => button.dataset.popupActionRun === actionValue)
    if (!goButton || !actionValue) return

    const [mappingId, actionName] = actionValue.split(":", 2)
    const mapping = popupIntegrationMappings().find((candidate) => candidate.id === mappingId)
    if (!mapping || !actionName || !mapping.action_url_template) return
    const actionDefinition = Array.from(mapping.action_definitions || []).find((definition) => definition.name === actionName)
    if (!actionDefinition) return

    state.popupActionFeedback = { tone: "working", message: "Running action..." }
    controller.renderPopup()

    try {
      const actionPayload = popupActionPayload(actionValue, actionDefinition)
      const payload = await apiRequest(
        mapping.action_url_template.replace("__ACTION__", encodeURIComponent(actionName)),
        {
          method: "POST",
          body: JSON.stringify(actionPayload)
        }
      )

      const result = payload.result || {}
      const sequenceOpened = result.open_sequence ? openActionSequence(result.open_sequence) : false
      if (!sequenceOpened && result.open_url) {
        try {
          window.open(
            result.open_url,
            result.open_window_target || "_blank",
            result.open_window_features || "noopener"
          )
        } catch (_error) {
          // Leave a fallback link in the action feedback if the browser blocks the popup.
        }
      }
      state.popupActionFeedback = {
        tone: result.ok === false ? "error" : "success",
        message: result.message || "Action completed successfully.",
        openUrl: result.open_url || "",
        openLabel: result.open_label || "",
        inlineEmbedUrl: result.inline_embed_url || "",
        inlineEmbedLabel: result.inline_embed_label || ""
      }
      state.liveDetailsByMappingId.delete(mapping.id)
      loadPopupLiveDetails()
    } catch (error) {
      state.popupActionFeedback = {
        tone: "error",
        message: error.message || "Action failed."
      }
    }

    controller.renderPopup()
  }

  const renderPopup = () => {
    if (!popup || !popupTitle || !popupMeta || !popupAttributes) return
    if (state.mappingMode || state.sidePanelMode === "mapping") {
      controller.closePopup()
      return
    }
    if (!state.popupEntry) {
      popup.setAttribute("hidden", "hidden")
      return
    }

    try {
      const entry = state.popupEntry
      const thing = entry.thing || {}
      const integrationMappings = popupIntegrationMappings()
      const entryKey = `${entry.kind}:${entry.id || entry.model_node_key || thing.id || thing.name || "entry"}`

      if (popupKicker) popupKicker.textContent = entry.kind === "placement" ? "Placed Object" : "Mapped Thing"
      popupTitle.textContent = thing.name || "Thing"
      popupMeta.hidden = true
      popupMeta.innerHTML = ""

      const objectMetaMarkup = entry.kind === "placement"
        ? `
          <dl class="plan-viewer-popup-meta plan-viewer-popup-meta-panel">
            <dt>Object</dt>
            <dd>${escapeHtml(thing.name || "Unnamed object")}</dd>
            <dt>Type</dt>
            <dd>${escapeHtml(formatDetailValue(thing.kind))}</dd>
            <dt>Vertical Position</dt>
            <dd>${escapeHtml(normalizePlacementVerticalMode(entry.metadata?.vertical_mode) === "ceiling" ? "Ceiling" : "On Surface")}</dd>
            <dt>Description</dt>
            <dd>${escapeHtml(formatDetailValue(thing.description))}</dd>
            <dt>Position</dt>
            <dd>${escapeHtml(formatPosition(entry.render_position || entry.position))}</dd>
          </dl>
        `
        : `
          <dl class="plan-viewer-popup-meta plan-viewer-popup-meta-panel">
            <dt>Node</dt>
            <dd>${escapeHtml(entry.model_node_name || entry.model_node_key || "Unknown node")}</dd>
            <dt>Key</dt>
            <dd>${escapeHtml(entry.model_node_key || "-")}</dd>
            <dt>Type</dt>
            <dd>${escapeHtml(formatDetailValue(thing.kind))}</dd>
            <dt>Description</dt>
            <dd>${escapeHtml(formatDetailValue(thing.description))}</dd>
          </dl>
        `

      const attributes = thing.attributes || {}
      const attributeEntries = Object.entries(attributes)
      const integrationMarkup = integrationMappings.length === 0
        ? `
          <div class="plan-viewer-popup-attribute">
            <span class="plan-viewer-popup-attribute-label">Integration Mappings</span>
            <span class="plan-viewer-popup-attribute-value">No integration mappings</span>
          </div>
        `
        : integrationMappings
            .map((mapping) => `
              <div class="plan-viewer-popup-attribute">
                <span class="plan-viewer-popup-attribute-label">${escapeHtml(mapping.integration_name || "")}</span>
                <span class="plan-viewer-popup-attribute-value">${escapeHtml(mapping.object_label || "")}</span>
                <span class="plan-viewer-popup-attribute-value">${escapeHtml(mapping.external_label || mapping.external_id || "")}</span>
              </div>
            `)
            .join("")

      const actionCards = integrationMappings.flatMap((mapping) =>
        Array.from(mapping.action_definitions || [])
          .filter(Boolean)
          .map((actionDefinition) => {
            const actionName = String(actionDefinition.name || "")
            const actionLabel = String(actionDefinition.label || actionName.replace(/_/g, " "))
            return {
              value: `${mapping.id}:${actionName}`,
              label: actionLabel,
              mapping,
              actionDefinition
            }
          })
      )
      if (state.popupSelectedAction && !actionCards.some((option) => option.value === state.popupSelectedAction)) {
        state.popupSelectedAction = ""
        state.popupActionValues = {}
        state.popupActionFeedback = null
      }

      const defaultTab = defaultPopupTabForMappings(integrationMappings, actionCards)
      if (state.popupTabEntryKey !== entryKey) {
        state.popupTabEntryKey = entryKey
        state.popupActiveTab = defaultTab
        state.popupMaintainDraft = defaultPopupMaintainDraft()
        state.popupMaintainFiles = []
      } else {
        state.popupActiveTab = normalizePopupTab(state.popupActiveTab)
      }

      const actionsSectionMarkup = actionCards.length === 0
        ? ""
        : `
          <div class="plan-viewer-popup-section">
            <div class="plan-viewer-popup-actions plan-viewer-popup-actions-remote">
              ${actionCards.map(({ value, label, mapping, actionDefinition }) => `
                <div class="plan-viewer-popup-action-control">
                  ${popupActionFieldsMarkup(value, actionDefinition)}
                  <button
                    type="button"
                    class="btn btn-ghost plan-viewer-popup-actions-go"
                    data-popup-action-run="${escapeHtml(value)}">
                    ${escapeHtml(label)}
                  </button>
                </div>
              `).join("")}
              ${popupActionMessage()}
              ${popupActionInlineEmbedMarkup()}
            </div>
          </div>
        `

      const liveDetailsSectionMarkup = integrationMappings.length === 0
        ? ""
        : `
          <div class="plan-viewer-popup-section">
            ${integrationMappings
              .map((mapping) => {
                const detailsState = state.liveDetailsByMappingId.get(mapping.id)

                if (!mapping.details_url) {
                  return `
                    <div class="plan-viewer-popup-attribute">
                      <span class="plan-viewer-popup-attribute-label">${escapeHtml(mapping.integration_name || "")}</span>
                      <span class="plan-viewer-popup-attribute-value">No live details configured.</span>
                    </div>
                  `
                }

                if (!detailsState || detailsState.status === "loading") {
                  return `
                    <div class="plan-viewer-popup-live-card">
                      <div class="plan-viewer-popup-live-head">
                        <div class="plan-viewer-popup-live-head-brand">
                          ${mapping.integration_icon_url
                            ? `<img class="plan-viewer-popup-live-head-logo" src="${escapeHtml(mapping.integration_icon_url)}" alt="${escapeHtml(mapping.integration_name || "Integration")} logo">`
                            : ""}
                          <span class="plan-viewer-popup-live-title">${escapeHtml(mapping.integration_name || "")}</span>
                        </div>
                        <span class="plan-viewer-popup-live-subtitle">${escapeHtml(mapping.object_label || "")}</span>
                      </div>
                      <div class="plan-viewer-skeleton-block plan-viewer-skeleton-line is-wide"></div>
                      <div class="plan-viewer-skeleton-block plan-viewer-skeleton-line"></div>
                      <div class="plan-viewer-skeleton-block plan-viewer-skeleton-line is-short"></div>
                    </div>
                  `
                }

                if (detailsState.status === "error") {
                  return `
                    <div class="plan-viewer-popup-attribute">
                      <span class="plan-viewer-popup-attribute-label">${escapeHtml(mapping.integration_name || "")}</span>
                      <span class="plan-viewer-popup-attribute-value">${escapeHtml(detailsState.error || "Could not load details.")}</span>
                    </div>
                  `
                }

                const details = detailsState.details || {}
                const blocksMarkup = renderLiveDetailBlocks(details.blocks, mapping)

                return `
                  <div class="plan-viewer-popup-live-card">
                    <div class="plan-viewer-popup-live-head">
                      <span class="plan-viewer-popup-live-title">${escapeHtml(details.title || mapping.integration_name || "")}</span>
                      <span class="plan-viewer-popup-live-subtitle">${escapeHtml(mapping.external_label || mapping.external_id || "")}</span>
                    </div>
                    ${blocksMarkup}
                  </div>
                `
              })
              .join("")}
          </div>
        `

      const attributesMarkup = attributeEntries.length === 0
        ? `
          <div class="plan-viewer-popup-attribute">
            <span class="plan-viewer-popup-attribute-label">Attributes</span>
            <span class="plan-viewer-popup-attribute-value">No extra attributes</span>
          </div>
        `
        : attributeEntries
            .map(([key, value]) => `
              <div class="plan-viewer-popup-attribute">
                <span class="plan-viewer-popup-attribute-label">${escapeHtml(key)}</span>
                <span class="plan-viewer-popup-attribute-value">${escapeHtml(formatDetailValue(value))}</span>
              </div>
            `)
            .join("")

      const popupTabMarkup = popupTabOptions()
        .map((option) => `
          <button
            type="button"
            class="plan-viewer-popup-tab${option.key === state.popupActiveTab ? " is-active" : ""}"
            data-popup-tab="${escapeHtml(option.key)}"
            aria-pressed="${option.key === state.popupActiveTab ? "true" : "false"}">
            ${escapeHtml(option.label)}
          </button>
        `)
        .join("")

      const statusPanelMarkup = liveDetailsSectionMarkup || `
        <div class="plan-viewer-popup-section">
          <div class="plan-viewer-popup-attribute">
            <span class="plan-viewer-popup-attribute-label">Live Integration Data</span>
            <span class="plan-viewer-popup-attribute-value">No live integration data available.</span>
          </div>
        </div>
      `

      const objectPanelMarkup = `
        <div class="plan-viewer-popup-section">
          ${objectMetaMarkup}
        </div>
        <div class="plan-viewer-popup-section">
          <h3 class="plan-viewer-popup-section-title">Integration Mappings</h3>
          ${integrationMarkup}
        </div>
      `

      const actionsPanelMarkup = actionsSectionMarkup || `
        <div class="plan-viewer-popup-section">
          <div class="plan-viewer-popup-attribute">
            <span class="plan-viewer-popup-attribute-label">Actions</span>
            <span class="plan-viewer-popup-attribute-value">No actions available for this selection.</span>
          </div>
        </div>
      `

      const attributesPanelMarkup = `
        <div class="plan-viewer-popup-section">
          <h3 class="plan-viewer-popup-section-title">Asset</h3>
          ${attributesMarkup}
        </div>
      `

      const maintainPanelMarkup = `
        <div class="plan-viewer-popup-section">
          <h3 class="plan-viewer-popup-section-title">Maintain</h3>
          <p class="plan-viewer-popup-actions-help">UI scaffold only. These details are not saved yet.</p>
          <div class="plan-viewer-popup-maintain-grid">
            <label class="plan-viewer-popup-actions-label" for="plan-viewer-popup-maintain-provider">Service Provider</label>
            <input
              id="plan-viewer-popup-maintain-provider"
              class="plan-mapping-input"
              data-popup-maintain-field="service_provider"
              type="text"
              value="${escapeHtml(state.popupMaintainDraft.service_provider || "")}"
              placeholder="Acme Services">

            <label class="plan-viewer-popup-actions-label" for="plan-viewer-popup-maintain-contact">Service Contact</label>
            <input
              id="plan-viewer-popup-maintain-contact"
              class="plan-mapping-input"
              data-popup-maintain-field="service_contact"
              type="text"
              value="${escapeHtml(state.popupMaintainDraft.service_contact || "")}"
              placeholder="Jordan Smith">

            <label class="plan-viewer-popup-actions-label" for="plan-viewer-popup-maintain-email">Contact Email</label>
            <input
              id="plan-viewer-popup-maintain-email"
              class="plan-mapping-input"
              data-popup-maintain-field="service_email"
              type="email"
              value="${escapeHtml(state.popupMaintainDraft.service_email || "")}"
              placeholder="service@example.com">

            <label class="plan-viewer-popup-actions-label" for="plan-viewer-popup-maintain-phone">Contact Phone</label>
            <input
              id="plan-viewer-popup-maintain-phone"
              class="plan-mapping-input"
              data-popup-maintain-field="service_phone"
              type="text"
              value="${escapeHtml(state.popupMaintainDraft.service_phone || "")}"
              placeholder="+61 400 000 000">

            <label class="plan-viewer-popup-actions-label" for="plan-viewer-popup-maintain-note">Note</label>
            <textarea
              id="plan-viewer-popup-maintain-note"
              class="plan-mapping-input plan-mapping-textarea"
              data-popup-maintain-field="note"
              rows="4"
              placeholder="Add maintenance notes, service history, or access instructions.">${escapeHtml(state.popupMaintainDraft.note || "")}</textarea>

            <label class="plan-viewer-popup-actions-label" for="plan-viewer-popup-maintain-link">Resource Link</label>
            <input
              id="plan-viewer-popup-maintain-link"
              class="plan-mapping-input"
              data-popup-maintain-field="resource_link"
              type="url"
              value="${escapeHtml(state.popupMaintainDraft.resource_link || "")}"
              placeholder="https://example.com/manual.pdf">

            <label class="plan-viewer-popup-actions-label" for="plan-viewer-popup-maintain-files">Attachments</label>
            <input
              id="plan-viewer-popup-maintain-files"
              class="plan-mapping-input"
              data-popup-maintain-files
              type="file"
              multiple>
            <div class="plan-viewer-popup-maintain-files">
              ${state.popupMaintainFiles.length === 0
                ? `<p class="plan-viewer-popup-actions-help">No files selected yet.</p>`
                : state.popupMaintainFiles.map((name) => `<span class="plan-viewer-popup-maintain-file">${escapeHtml(name)}</span>`).join("")}
            </div>
          </div>
        </div>
      `

      popupAttributes.innerHTML = `
        <div class="plan-viewer-popup-tabs" role="tablist" aria-label="Object detail sections">
          ${popupTabMarkup}
        </div>
        <div class="plan-viewer-popup-tab-panels">
          <section class="plan-viewer-popup-tab-panel${state.popupActiveTab === "status" ? " is-active" : ""}" data-popup-tab-panel="status"${state.popupActiveTab === "status" ? "" : " hidden"}>
            ${statusPanelMarkup}
          </section>
          <section class="plan-viewer-popup-tab-panel${state.popupActiveTab === "actions" ? " is-active" : ""}" data-popup-tab-panel="actions"${state.popupActiveTab === "actions" ? "" : " hidden"}>
            ${actionsPanelMarkup}
          </section>
          <section class="plan-viewer-popup-tab-panel${state.popupActiveTab === "attributes" ? " is-active" : ""}" data-popup-tab-panel="attributes"${state.popupActiveTab === "attributes" ? "" : " hidden"}>
            ${attributesPanelMarkup}
          </section>
          <section class="plan-viewer-popup-tab-panel${state.popupActiveTab === "maintain" ? " is-active" : ""}" data-popup-tab-panel="maintain"${state.popupActiveTab === "maintain" ? "" : " hidden"}>
            ${maintainPanelMarkup}
          </section>
          <section class="plan-viewer-popup-tab-panel${state.popupActiveTab === "object" ? " is-active" : ""}" data-popup-tab-panel="object"${state.popupActiveTab === "object" ? "" : " hidden"}>
            ${objectPanelMarkup}
          </section>
        </div>
      `

      popup.querySelectorAll("[data-popup-tab]").forEach((button) => {
        button.addEventListener("click", () => {
          state.popupActiveTab = normalizePopupTab(button.dataset.popupTab)
          controller.renderPopup()
        })
      })

      popup.querySelectorAll("[data-popup-maintain-field]").forEach((input) => {
        const syncMaintainField = () => {
          state.popupMaintainDraft[input.dataset.popupMaintainField] = input.value
        }

        input.addEventListener("input", syncMaintainField)
        input.addEventListener("change", syncMaintainField)
      })

      popup.querySelector("[data-popup-maintain-files]")?.addEventListener("change", (event) => {
        state.popupMaintainFiles = Array.from(event.target.files || []).map((file) => file.name)
        controller.renderPopup()
      })

      const syncPopupActionButtons = () => {
        popup.querySelectorAll("[data-popup-action-run]").forEach((button) => {
          const actionValue = button.dataset.popupActionRun
          const [_mappingId, actionName] = actionValue.split(":", 2)
          const mapping = popupIntegrationMappings().find((candidate) => actionValue.startsWith(`${candidate.id}:`))
          const actionDefinition = Array.from(mapping?.action_definitions || []).find((definition) => definition.name === actionName)
          button.disabled = state.popupActionFeedback?.tone === "working" || !popupActionFieldsValid(actionValue, actionDefinition)
        })
      }

      popup.querySelectorAll("[data-popup-action-field]").forEach((input) => {
        const syncActionField = () => {
          const actionValue = input.dataset.popupActionKey
          const fieldKey = input.dataset.popupActionField
          state.popupActionValues[popupActionStateKey(actionValue, fieldKey)] = input.type === "checkbox" ? (input.checked ? "true" : "false") : input.value
          const rangeValue = popup.querySelector(`[data-popup-action-range-value="${actionValue}:${fieldKey}"]`)
          if (rangeValue) rangeValue.textContent = input.value
          syncPopupActionButtons()
        }

        input.addEventListener("input", syncActionField)
        input.addEventListener("change", syncActionField)
      })
      popup.querySelector("[data-popup-inline-embed-close]")?.addEventListener("click", () => {
        if (!state.popupActionFeedback) return

        state.popupActionFeedback = {
          ...state.popupActionFeedback,
          inlineEmbedUrl: "",
          inlineEmbedLabel: ""
        }
        controller.renderPopup()
      })
      popup.querySelectorAll("[data-popup-action-run]").forEach((button) => {
        button.addEventListener("click", () => {
          executePopupAction(button.dataset.popupActionRun)
        })
      })
      syncPopupActionButtons()

      popup.removeAttribute("hidden")
      loadPopupLiveDetails()
    } catch (error) {
      console.error("Could not render viewer popup", error)
      popupMeta.innerHTML = ""
      popupMeta.hidden = true
      popupAttributes.innerHTML = `
        <div class="plan-viewer-popup-attribute">
          <span class="plan-viewer-popup-attribute-label">Details</span>
          <span class="plan-viewer-popup-attribute-value">Could not render details.</span>
        </div>
      `
      popup.removeAttribute("hidden")
    }
  }

  controller.closePopup = () => {
    state.popupEntry = null
    state.popupActiveTab = "status"
    state.popupTabEntryKey = null
    state.popupSelectedAction = ""
    state.popupActionFeedback = null
    state.popupActionValues = {}
    state.popupMaintainDraft = {}
    state.popupMaintainFiles = []
    popup?.setAttribute("hidden", "hidden")
  }
  controller.loadPopupLiveDetails = loadPopupLiveDetails
  controller.refreshPopupLiveDetails = refreshPopupLiveDetails
  controller.executePopupAction = executePopupAction
  controller.renderPopup = renderPopup

  return controller
}
